import React, { useState, useEffect, useRef, useReducer, useCallback, memo, useContext } from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  Mail, 
  Calendar, 
  MapPin, 
  Briefcase, 
  Edit2, 
  Save, 
  X, 
  Loader,
  Phone,
  Clock,
  Building
} from 'lucide-react';
import userProfileService from '../../services/userProfileService';
import { AuthContext } from '../../contexts/AuthContext';

// List of all countries with priority countries at the top
const COUNTRIES = [
  // Priority countries
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  // Separator
  { code: 'divider', name: '──────────' },
  // Other countries
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  // ... add needed countries alphabetically
];

const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FIELD':
      return {
        ...state,
        [action.field]: action.value
      };
    case 'RESET':
      return action.values;
    default:
      return state;
  }
};

const MemoizedEditableInput = memo(({ name, value, reference, onChange, type = 'text' }) => {
  const handleChange = useCallback((e) => {
    e.stopPropagation();
    onChange(name, e.target.value);
  }, [name, onChange]);

  if (type === 'country') {
    return (
      <select
        ref={reference}
        name={name}
        value={value}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        className="text-sm text-gray-900 flex-1 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      >
        <option value="">Select a country</option>
        {COUNTRIES.map((country) => (
          country.code === 'divider' ? (
            <option key={country.code} disabled>{country.name}</option>
          ) : (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          )
        ))}
      </select>
    );
  }

  return (
    <input
      ref={reference}
      type="text"
      name={name}
      value={value}
      onChange={handleChange}
      onFocus={(e) => {
        e.stopPropagation();
        e.target.select();
      }}
      onClick={(e) => e.stopPropagation()}
      className="text-sm text-gray-900 flex-1 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      autoComplete="off"
    />
  );
});

MemoizedEditableInput.displayName = 'MemoizedEditableInput';

const MemoizedInfoItem = memo(({ icon: Icon, label, name, value, editable, inputRef, onChange, isEditing, type }) => {
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (editable && isEditing) {
      inputRef?.current?.focus();
    }
  }, [editable, isEditing, inputRef]);

  // Function to display country name instead of code
  const displayValue = useCallback((val) => {
    if (type === 'country' && val) {
      const country = COUNTRIES.find(c => c.code === val);
      return country ? country.name : val;
    }
    return val;
  }, [type]);

  return (
    <div className="flex items-center space-x-3 py-2" onClick={handleClick}>
      <Icon className="w-5 h-5 text-primary-600" />
      <span className="text-sm font-medium text-gray-500">{label}:</span>
      {isEditing && editable ? (
        <MemoizedEditableInput
          name={name}
          value={value}
          reference={inputRef}
          onChange={onChange}
          type={type}
        />
      ) : (
        <span className="text-sm text-gray-900">{displayValue(value) || 'N/A'}</span>
      )}
    </div>
  );
});

MemoizedInfoItem.displayName = 'MemoizedInfoItem';

const UserProfile = ({ embedded = false }) => {
  console.log('UserProfile component rendering');

  const { user } = useContext(AuthContext);
  // TODO: Extend to check for specific permissions (e.g., 'manage_enhanced_profile')
  // For now, only superusers see enhanced fields (phone, timezone, institution)
  const isSuperuser = user?.is_superuser || false;

  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [formData, dispatch] = useReducer(formReducer, {
    first_name: '',
    last_name: '',
    country: '',
    phone_number: '',
    timezone: '',
    institution_name: ''
  });

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const countryRef = useRef(null);
  const phoneRef = useRef(null);
  const timezoneRef = useRef(null);
  const institutionRef = useRef(null);


  // Load initial profile data
  useEffect(() => {
    console.log('Component mounted');
    const fetchProfile = async () => {
      console.log('Fetching profile data...');
      try {
        const userData = await userProfileService.getUserProfile();
        console.log('Received profile data:', userData);
        
        // Update both profile and form data
        setProfile(userData);
        
        // Extract profile fields from userData.profile
        const profileData = userData.profile || {};
        dispatch({
          type: 'RESET',
          values: {
            first_name: profileData.first_name || '',
            last_name: profileData.last_name || '',
            country: profileData.country || '',
            phone_number: profileData.phone_number || '',
            timezone: profileData.timezone || '',
            institution_name: profileData.institution_name || ''
          }
        });
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setError('An error occurred while fetching the data.');
      }
    };

    fetchProfile();
    return () => {
      console.log('Component unmounting');
    };
  }, []);

  const handleFieldChange = useCallback((field, value) => {
    dispatch({ type: 'SET_FIELD', field, value });
  }, []);

  const handleSubmit = async (e) => {
    console.log('handleSubmit called');
    e.preventDefault();
    try {
      console.log('Submitting form data:', formData);
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const updatedProfile = await userProfileService.updateUserProfile(formData);
      console.log('Received updated profile:', updatedProfile);
      
      // Update both profile and form data with the response
      setProfile(updatedProfile);
      const profileData = updatedProfile.profile || {};
      dispatch({
        type: 'RESET',
        values: {
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          country: profileData.country || '',
          phone_number: profileData.phone_number || '',
          timezone: profileData.timezone || '',
          institution_name: profileData.institution_name || ''
        }
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.status === 401) {
        // Handle authentication error
        setError('Please log in again to update your profile.');
        // Optionally redirect to login
        // window.location.href = '/login';
      } else {
        setError('An error occurred while updating the profile.');
      }
    }
  };

  const handleEditToggle = useCallback(() => {
    console.log('handleEditToggle called, current isEditing:', isEditing);
    if (isEditing) {
      console.log('Resetting form data to profile values');
      const profileData = profile.profile || {};
      dispatch({
        type: 'RESET',
        values: {
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          country: profileData.country || '',
          phone_number: profileData.phone_number || '',
          timezone: profileData.timezone || '',
          institution_name: profileData.institution_name || ''
        }
      });
    }
    setIsEditing(!isEditing);
    setError('');
  }, [isEditing, profile]);

  if (error) {
    console.log('Error occurred:', error);
    return (
      <div className={embedded ? "p-8" : "flex justify-center items-center h-screen"}>
        <div className={embedded ? "" : "bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl"}>
          <h1 className="text-xl font-bold text-red-600 mb-2">Error</h1>
          <p className="text-gray-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    console.log('Profile data not available yet');
    return (
      <div className={embedded ? "flex justify-center items-center py-16" : "flex justify-center items-center h-screen"}>
        <div className="flex flex-col items-center">
          <Loader className="w-10 h-10 text-primary-600 animate-spin mb-3" />
          <p className="text-gray-500 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  console.log('Rendering profile data:', profile);

  // Embedded version - clean styling
  if (embedded) {
    return (
      <div className="p-6">
        {/* Header with Edit Button */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Personal Information</h2>
            <p className="text-sm text-gray-500 mt-0.5">Update your personal details</p>
          </div>
          <button 
            onClick={handleEditToggle}
            className={`px-4 py-2 rounded-lg flex items-center text-sm font-medium transition-colors ${
              isEditing 
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {isEditing ? (
              <><X className="w-4 h-4 mr-2" />Cancel</>
            ) : (
              <><Edit2 className="w-4 h-4 mr-2" />Edit</>
            )}
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Profile Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">First Name</label>
              {isEditing ? (
                <input
                  ref={firstNameRef}
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleFieldChange('first_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              ) : (
                <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100">{formData.first_name || '—'}</p>
              )}
            </div>

            {/* Last Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Last Name</label>
              {isEditing ? (
                <input
                  ref={lastNameRef}
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleFieldChange('last_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              ) : (
                <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100">{formData.last_name || '—'}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Email Address</label>
              <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100">{profile.email}</p>
            </div>

            {/* Role */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Role</label>
              <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100">{profile.role || 'User'}</p>
            </div>

            {/* Country */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Country</label>
              {isEditing ? (
                <select
                  ref={countryRef}
                  name="country"
                  value={formData.country}
                  onChange={(e) => handleFieldChange('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select a country</option>
                  {COUNTRIES.map((country) => (
                    country.code === 'divider' ? (
                      <option key={country.code} disabled>{country.name}</option>
                    ) : (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    )
                  ))}
                </select>
              ) : (
                <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100">
                  {formData.country ? COUNTRIES.find(c => c.code === formData.country)?.name || formData.country : '—'}
                </p>
              )}
            </div>

            {/* Member Since */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Member Since</label>
              <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100">
                {new Date(profile.date_joined).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>

          {/* Enhanced Profile Fields - Superuser Only */}
          {isSuperuser && (
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Phone */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">Phone Number</label>
                  {isEditing ? (
                    <input
                      ref={phoneRef}
                      type="text"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={(e) => handleFieldChange('phone_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100">{formData.phone_number || '—'}</p>
                  )}
                </div>

                {/* Timezone */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">Timezone</label>
                  {isEditing ? (
                    <input
                      ref={timezoneRef}
                      type="text"
                      name="timezone"
                      value={formData.timezone}
                      onChange={(e) => handleFieldChange('timezone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100">{formData.timezone || '—'}</p>
                  )}
                </div>

                {/* Institution */}
                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Institution</label>
                  {isEditing ? (
                    <input
                      ref={institutionRef}
                      type="text"
                      name="institution_name"
                      value={formData.institution_name}
                      onChange={(e) => handleFieldChange('institution_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100">{formData.institution_name || '—'}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isEditing && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-6 flex justify-end"
            >
              <button
                type="submit"
                className="bg-primary-600 text-white px-6 py-2.5 rounded-lg hover:bg-primary-700 transition-colors flex items-center font-medium"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </button>
            </motion.div>
          )}
        </form>
      </div>
    );
  }

  // Standalone page version
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <User className="w-6 h-6 text-primary-600 mr-3" />
              User Profile
            </h1>
          </div>
        </motion.div>

        <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm">
            <div className="px-6 py-4 bg-primary-600 text-white flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">Personal Information</h2>
              <button 
                onClick={handleEditToggle}
                className="bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-300 flex items-center text-sm font-medium"
              >
                {isEditing ? (
                  <><X className="w-4 h-4 mr-2" />Cancel</>
                ) : (
                  <><Edit2 className="w-4 h-4 mr-2" />Edit Profile</>
                )}
              </button>
            </div>
            <div className="p-6">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-4">
                  <MemoizedInfoItem 
                    icon={User}
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    editable={true}
                    inputRef={firstNameRef}
                    onChange={handleFieldChange}
                    isEditing={isEditing}
                  />
                  <MemoizedInfoItem 
                    icon={User}
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    editable={true}
                    inputRef={lastNameRef}
                    onChange={handleFieldChange}
                    isEditing={isEditing}
                  />
                  <MemoizedInfoItem 
                    icon={Mail}
                    label="Email"
                    name="email"
                    value={profile.email}
                    editable={false}
                    isEditing={isEditing}
                  />
                </div>
                <div className="space-y-4">
                  <MemoizedInfoItem 
                    icon={Briefcase}
                    label="Role"
                    name="role"
                    value={profile.role}
                    editable={false}
                    isEditing={isEditing}
                  />
                  <MemoizedInfoItem 
                    icon={MapPin}
                    label="Country"
                    name="country"
                    value={formData.country}
                    editable={true}
                    inputRef={countryRef}
                    onChange={handleFieldChange}
                    isEditing={isEditing}
                    type="country"
                  />
                  <MemoizedInfoItem 
                    icon={Calendar}
                    label="Joined"
                    name="date_joined"
                    value={new Date(profile.date_joined).toLocaleDateString('en-US', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                    editable={false}
                    isEditing={isEditing}
                  />
                </div>
              </div>

              {/* Enhanced Profile Fields - Superuser Only */}
              {isSuperuser && (
                <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                      <Building className="w-5 h-5 mr-2 text-primary-600" />
                      Additional Information
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Enhanced profile options for administrators</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div className="space-y-4">
                      <MemoizedInfoItem 
                        icon={Phone}
                        label="Phone Number"
                        name="phone_number"
                        value={formData.phone_number}
                        editable={true}
                        inputRef={phoneRef}
                        onChange={handleFieldChange}
                        isEditing={isEditing}
                      />
                      <MemoizedInfoItem 
                        icon={Building}
                        label="Institution"
                        name="institution_name"
                        value={formData.institution_name}
                        editable={true}
                        inputRef={institutionRef}
                        onChange={handleFieldChange}
                        isEditing={isEditing}
                      />
                    </div>
                    <div className="space-y-4">
                      <MemoizedInfoItem 
                        icon={Clock}
                        label="Timezone"
                        name="timezone"
                        value={formData.timezone}
                        editable={true}
                        inputRef={timezoneRef}
                        onChange={handleFieldChange}
                        isEditing={isEditing}
                      />
                    </div>
                  </div>
                </div>
              )}
              {isEditing && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-6"
                >
                  <button
                    type="submit"
                    className="w-full bg-primary-600 text-white px-6 py-3 rounded-full hover:bg-primary-700 transition duration-300 flex items-center justify-center"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    Save Changes
                  </button>
                </motion.div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;